
import React from "react";
import { motion } from "framer-motion";
import {
  Settings as SettingsIcon,
  Bell,
  Palette,
  Droplets,
  Eye,
  PersonStanding,
  Wind,
  ChevronRight,
  UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store";

/* =========================================================
   GENERAL SETTINGS
========================================================= */

const GENERAL_SETTINGS = [
  {
    label: "Profile & Account",
    description: "Your name, photo, password and preferences",
    icon: UserRound,
    path: "/settings/profile",
  },
  {
    label: "Notifications",
    description: "Manage wellness notifications and reminders",
    icon: Bell,
    path: "/settings/notifications",
  },
  {
    label: "Appearance",
    description: "Customize the look and feel of Wellness Garden",
    icon: Palette,
    path: "/settings/appearance",
  },
];

/* =========================================================
   WELLNESS SETTINGS
========================================================= */

const WELLNESS_SETTINGS = [
  {
    label: "Drink Water",
    description: "Customize your daily water goal and reminders",
    icon: Droplets,
    path: "/settings/water",
  },
  {
    label: "Eye Care",
    description: "Manage eye break reminders and schedules",
    icon: Eye,
    path: "/settings/eye-care",
  },
  {
    label: "Move & Reset",
    description: "Manage movement reminders and schedules",
    icon: PersonStanding,
    path: "/settings/move-reset",
  },
  {
    label: "Breathe",
    description: "Manage breathing reminders and schedules",
    icon: Wind,
    path: "/settings/breathe",
  },
];

/* =========================================================
   SETTING ITEM
========================================================= */

const SettingItem = ({ item }) => {
  const navigate = useNavigate();
  const Icon = item.icon;

  return (
    <motion.button
      type="button"
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate(item.path)}
      className="w-full flex items-center gap-4 p-4 text-left transition-colors"
      style={{
        color: "var(--cozy-text)",
      }}
    >
     {/* ICON */}
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: "var(--cozy-primary)",
            color: "#ffffff",
            border: "1px solid var(--cozy-primary)",
          }}
        >
          <Icon
            className="w-5 h-5"
            strokeWidth={2.5}
          />
        </div>

      {/* TEXT */}
      <div className="flex-1 min-w-0">
        <h3
          className="font-bold text-sm"
          style={{
            color: "var(--cozy-text)",
          }}
        >
          {item.label}
        </h3>

        <p
          className="text-xs mt-1"
          style={{
            color: "var(--cozy-text)",
            opacity: 0.6,
          }}
        >
          {item.description}
        </p>
      </div>

      {/* ARROW */}
      <ChevronRight
        className="w-5 h-5 opacity-50 shrink-0"
        style={{
          color: "var(--cozy-text)",
        }}
      />
    </motion.button>
  );
};

/* =========================================================
   SETTINGS SECTION
========================================================= */

const SettingsSection = ({ title, items }) => {
  return (
    <section>
      <h2
        className="px-2 mb-3 text-xs font-black uppercase tracking-widest"
        style={{
          color: "var(--cozy-text)",
          opacity: 0.5,
        }}
      >
        {title}
      </h2>

      <div
        className="overflow-hidden shadow-cozy"
        style={{
          background: "var(--cozy-surface)",
          border: "1px solid var(--cozy-border)",
          borderRadius: 20,
        }}
      >
        {items.map((item, index) => (
          <div
            key={item.path}
            style={{
              borderBottom:
                index === items.length - 1
                  ? "none"
                  : "1px solid var(--cozy-border)",
            }}
          >
            <SettingItem item={item} />
          </div>
        ))}
      </div>
    </section>
  );
};

/* =========================================================
   SETTINGS PAGE
========================================================= */

const Settings = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === "admin";

  return (
    <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
      {/* HEADER */}
      <div className="flex items-center gap-4 mb-8">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: "var(--cozy-primary)",
            color: "var(--cozy-on-primary, #ffffff)",
          }}
        >
          <SettingsIcon className="w-7 h-7" />
        </div>

        <div>
          <h1
            className="settings-page-title font-display font-black text-3xl md:text-4xl"
            style={{
              color: "var(--cozy-text)",
            }}
          >
            Settings
          </h1>

          <p
            className="settings-page-description text-sm mt-1"
            style={{
              color: "var(--cozy-text)",
              opacity: 0.6,
            }}
          >
            Customize your Wellness Garden experience.
          </p>
        </div>
      </div>

      {/* SETTINGS SECTIONS */}
      <div className="space-y-8">
        {/* GENERAL */}
        <SettingsSection
          title="General"
          items={GENERAL_SETTINGS}
        />

        {/* WELLNESS */}
        <SettingsSection
          title="Wellness"
          items={WELLNESS_SETTINGS}
        />
      </div>
    </main>
  );
};

export default Settings;
